<template>
  <textarea
    v-model="form.text"
    class="feedback-form-textarea"
    :class="{ 'feedback-form-textarea_shrunk': form.images.length > 0 }"
    :disabled="isSubmitting"
  />

  <div
    v-if="form.text.length === 0 && !isSubmitting"
    class="feedback-form-textarea-placeholder"
  >
    Введите текст обращения
  </div>

  <FeedbackFormImages
    :form="form"
    :disable-actions="isSubmitting"
  />

  <div class="feedback-form-input-footer">
    <div class="feedback-form-input-footer-actions">
      <FeedbackFormImagesInput
        :form="form"
        :is-disabled="isSubmitting"
        @set-images="form.images = $event"
      />

      <button
        v-if="!isSubmitting"
        class="feedback-form-input-submit-button"
        :class="{ 'feedback-form-input-submit-button_disabled': !isFormValid }"
        :disabled="!isFormValid"
        @click="submitForm"
      >
        Отправить
      </button>

      <CPreloader
        v-if="isSubmitting"
        size="small"
        no-height
      />
    </div>

    <!-- <div class="feedback-form-input-footer-captcha">
      This site is protected by reCAPTCHA and the Google
      <a href="https://policies.google.com/privacy">Privacy Policy</a>
      and
      <a href="https://policies.google.com/terms">Terms of Service</a>
      apply.
    </div> -->

    <div
      v-if="status !== null"
      class="feedback-form-input-status-text"
      :class="{
        'feedback-form-input-status-text_success': status === 'success',
        'feedback-form-input-status-text_error': status === 'error'
      }"
    >
      {{ statusText }}
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { reactive, ref, watch, computed } from 'vue';
  import { AxiosError } from 'axios';

  import { sendFeedbackForm } from '@/services/feedbackForm/api';

  import FeedbackFormImages from './FeedbackFormImages.vue';
  import { FeedbackForm } from './types/feedbackForm';
  import FeedbackFormImagesInput from './FeedbackFormImagesInput.vue';
  import CPreloader from '../CPreloader/CPreloader.vue';
  import { useCaptcha } from '@/composables/useCaptcha';

  const form: FeedbackForm = reactive({
    text: '',
    images: []
  });

  const statusText = ref<string | null>(null);
  const status = ref<'success' | 'error' | null>(null);

  const isSubmitting = ref(false);

  let timer: ReturnType<typeof setTimeout> | null = null;

  const isFormValid = computed(() => form.text.length > 0);

  async function submitForm() {
    if (!isFormValid.value) {
      return;
    }

    isSubmitting.value = true;

    const formData = new FormData();
    formData.append('description', form.text);

    form.images.forEach(file => {
      formData.append('files', file);
    });

    const token = await useCaptcha().execute();
    formData.append('g-recaptcha-response', token);

    try {
      const response = await sendFeedbackForm(formData);

      if (response.status !== 200) throw new Error('Network response was not ok');

      // Clear form
      form.text = '';
      form.images = [];

      status.value = 'success';
      statusText.value = 'Ваше обращение отправлено';

      timer = setTimeout(() => {
        status.value = null;
        statusText.value = null;
      }, 5000);
    } catch (error) {
      if (timer) {
        clearTimeout(timer);
      }

      status.value = 'error';

      if (
        error instanceof AxiosError &&
        error.isAxiosError &&
        error.response?.data?.error?.message
      ) {
        if (error.response?.data?.error?.message === 'Upload failed') {
          statusText.value = 'Размер изображения слишком большой';

          return;
        }
      }

      statusText.value = 'Ошибка при отправке формы';
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      isSubmitting.value = false;
    }
  }

  watch(
    () => [form.text, form.images.length],
    () => {
      if (form.text.length > 0 || form.images.length > 0) {
        status.value = null;
        statusText.value = null;
      }
    }
  );
</script>

<style>
  .feedback-form-textarea {
    width: calc(100% - 48px);
    flex: 1;
    display: block;
    border: none;
    padding: 12px 24px;
    font-size: 18px;
    outline: none;
    font-family: var(--g-font-family);
    font-weight: 500;
    background: #fff;

    &:disabled {
      opacity: 1;
      pointer-events: none;
      background: #fff;
    }

    &_shrunk {
      padding-bottom: 152px;
      height: calc(100% - 212px - 100px);
    }
  }

  .feedback-form-textarea-placeholder {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    font-weight: 600;
  }

  .feedback-form-input-footer {
    padding: 24px 24px 12px;
  }

  .feedback-form-input-submit-button {
    font-size: 24px;
    height: 46px;
    background: var(--g-color-orange);
    padding: 0 50px;
    border-radius: 25px;
    color: #fff;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    border: none;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }

    &_disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  .feedback-form-input-footer-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    &_disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  .feedback-form-input-status-text {
    font-size: 16px;
    padding: 16px 0 0;

    &_success {
      color: green;
    }

    &_error {
      color: red;
    }
  }

  .feedback-form-input-footer-captcha {
    padding-top: 8px;
    font-size: 10px;
    color: #999;
  }

  @media (--mobile) {
    .feedback-form-textarea {
      &-placeholder {
        text-align: center;
        top: 100px;
        bottom: auto;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 16px;
        margin: 0;
        font-weight: 600;
      }
    }

    .feedback-form-input-footer {
      padding: 24px 12px 12px;
    }

    /* .feedback-form-input-footer-actions {
    /* flex-direction: column;
    align-items: flex-start; */
    /* } */

    .feedback-form-input-footer-captcha {
      margin-top: 8px;
    }
  }
</style>
